import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle2, Clock, Loader2, Play, FileCheck } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import ToggleInput from '../components/ui/ToggleInput';

import { useToast } from '../hooks/useToast';
import { client, fetchBomHeaders, fetchRoutingHeaders, fetchTargetsInProduction, fetchUtilCustomers } from '../apiHelper';

export const ProductionOrderScheduler = ({ storeId, currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [productOptions, setProductOptions] = useState([]);
    const [bomOptions, setBomOptions] = useState([]);
    const [routingOptions, setRoutingOptions] = useState([]);

    const [customerOptions, setCustomerOptions] = useState([]);

    const [orderForm, setOrderForm] = useState({
        catentryId: '',
        bomId: '',
        routingId: '',
        quantityPlanned: '',
        startDatePlanned: '',
        endDatePlanned: '',
        status: 'draft',
        description: '',
        jobType: 'SPECULATIVE', // otherwise 'CONTRACT',
        customerId: '',
    });

    const jobTypeOptions = [
        { label: 'Speculative', value: 'SPECULATIVE' },
        { label: 'Contract', value: 'CONTRACT' },
    ]

    useEffect(() => {
        if (!storeId) return;
        const loadSchedulerOptions = async () => {
            setLoading(true);
            try {
                const payload = { ...currentUser, store_id: parseInt(storeId, 10), offset: 0, limit: 10000 };

                const [targets, boms, routings] = await Promise.all([
                    // fetchTargetsForProduction(payload).catch(() => []),
                    fetchTargetsInProduction(payload).catch(() => []),
                    fetchBomHeaders(parseInt(storeId, 10)).catch(() => []),
                    fetchRoutingHeaders(parseInt(storeId, 10)).catch(() => [])
                ]);

                try {
                    const user = currentUser || JSON.parse(localStorage.getItem('user') || '{}');
                    const customers = await fetchUtilCustomers(user);
                    const opts = (customers || []).map(c => {
                        const u = c.user || {};
                        const fullName = `${(u.first_name || '').trim()} ${(u.middle_name || '').trim()} ${(u.last_name || '').trim()}`.trim() || 'Unnamed Customer';
                        return {
                            label: fullName,
                            value: c.member_id.toString(),
                            customerData: c
                        };
                    });
                    setCustomerOptions(opts);
                } catch (err) {
                    console.error("Failed to fetch customers", err);
                }

                const itemMap = new Map();
                (Array.isArray(targets) ? targets : []).forEach(lot => {
                    if (lot?.catentry) {
                        itemMap.set(lot.catentry.catentry_id, {
                            label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                            value: lot.catentry.catentry_id.toString()
                        });
                    }
                });
                setProductOptions(Array.from(itemMap.values()));


                setBomOptions((boms || []).map(b => ({
                    label: `BOM Rev ${b.version} - ${b.description || 'Active Blueprint'}`,
                    value: b.bom_id.toString()
                })));

                setRoutingOptions((routings || []).map(r => ({
                    label: `Routing Rev ${r.version} - ${r.description || 'Standard Path'}`,
                    value: r.routing_id.toString()
                })));
            } catch (err) {
                showToast("Error loading scheduling options.", "error");
            } finally {
                setLoading(false);
            }
        };
        loadSchedulerOptions();
    }, [storeId, currentUser]);

    const handleCreateOrder = async (targetStatus) => {
        if (!orderForm.catentryId || !orderForm.quantityPlanned || !orderForm.startDatePlanned || !orderForm.endDatePlanned) {
            showToast("Please fill all required scheduling fields.", "error");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                catentry_id: parseInt(orderForm.catentryId, 10),
                store_id: parseInt(storeId, 10),
                bom_id: orderForm.bomId ? parseInt(orderForm.bomId, 10) : null,
                routing_id: orderForm.routingId ? parseInt(orderForm.routingId, 10) : null,
                job_type: orderForm.jobType,
                customer_id: orderForm.jobType === 'CONTRACT' && orderForm.customerId ? parseInt(orderForm.customerId, 10) : null,
                quantity_planned: parseFloat(orderForm.quantityPlanned),
                quantity_produced: 0.0,
                start_date_planned: new Date(orderForm.startDatePlanned).toISOString(),
                end_date_planned: new Date(orderForm.endDatePlanned).toISOString(),
                status: targetStatus,
                description: orderForm.description || null
            };
            const endpoint = targetStatus === 'confirmed'
                ? "/manufacturing/create-production-order-confirmed"
                : "/manufacturing/create-production-order-draft"
            // console.log(payload)
            await client.postData(endpoint, payload);
            showToast(`Production Order scheduled successfully in status: ${targetStatus.toUpperCase()}`, "success");
            setOrderForm({
                catentryId: '',
                bomId: '',
                routingId: '',
                quantityPlanned: '',
                startDatePlanned: '',
                endDatePlanned: '',
                status: 'draft',
                description: '',
                jobType: 'SPECULATIVE',
                customerId: '',
            });
        } catch (err) {
            showToast(err.response?.data?.detail || "Could not dispatch production order.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2 text-blue-600" /> Preparing dispatch terminal...</div>;

    return (
        <Card className="p-6 border-zinc-200 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6">
                <h2 className="text-lg font-bold text-zinc-900">Schedule & Dispatch Production Run</h2>
                <p className="text-xs text-zinc-500">Create work orders, link active BOM/Routing structures, and dispatch jobs to floor operations.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {/* <div className="md:col-span-3">
                    <SearchableSelectBox
                        label="Target Finished Product"
                        options={productOptions}
                        value={orderForm.catentryId}
                        onChange={(e) => setOrderForm(p => ({ ...p, catentryId: e.target.value }))}
                        placeholder="Select item to manufacture..."
                    />
                </div> */}
                <div className="md:col-span-2">
                    <SearchableSelectBox
                        label="Target Finished Product"
                        options={productOptions}
                        value={orderForm.catentryId}
                        onChange={(e) => setOrderForm(p => ({ ...p, catentryId: e.target.value }))}
                        placeholder="Select item to manufacture..."
                    />
                </div>

                <ToggleInput
                    label="Job Type"
                    options={jobTypeOptions}
                    value={orderForm.jobType}
                    onChange={(val) => setOrderForm(p => ({ ...p, jobType: val }))}
                />

                {orderForm.jobType === 'CONTRACT' && (
                    <SearchableSelectBox
                        label="Customer / Client"
                        options={customerOptions}
                        value={orderForm.customerId}
                        required
                        onChange={(e) => setOrderForm(p => ({ ...p, customerId: e.target.value }))}
                        placeholder="Select customer..."
                    />
                )}

                <SearchableSelectBox
                    label="Bill of Materials (BOM) Rev"
                    options={bomOptions}
                    value={orderForm.bomId}
                    onChange={(e) => setOrderForm(p => ({ ...p, bomId: e.target.value }))}
                    placeholder="Default System Standard"
                />

                <SearchableSelectBox
                    label="Routing Sequence Rev"
                    options={routingOptions}
                    value={orderForm.routingId}
                    onChange={(e) => setOrderForm(p => ({ ...p, routingId: e.target.value }))}
                    placeholder="Default Direct Path"
                />

                <Input
                    label="Planned Production Target Units"
                    type="number"
                    value={orderForm.quantityPlanned}
                    onChange={(e) => setOrderForm(p => ({ ...p, quantityPlanned: e.target.value }))}
                    placeholder="e.g. 1000"
                />

                <Input
                    label="Planned Start Date"
                    type="date"
                    value={orderForm.startDatePlanned}
                    onChange={(e) => setOrderForm(p => ({ ...p, startDatePlanned: e.target.value }))}
                />

                <Input
                    label="Planned Completion Date"
                    type="date"
                    value={orderForm.endDatePlanned}
                    onChange={(e) => setOrderForm(p => ({ ...p, endDatePlanned: e.target.value }))}
                />

                <div className="md:col-span-3">
                    <label className="block text-xs font-medium text-zinc-700 mb-1">Log Directives / Factory Directives</label>
                    <textarea
                        value={orderForm.description}
                        onChange={(e) => setOrderForm(p => ({ ...p, description: e.target.value }))}
                        rows={2}
                        className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-600 focus:outline-none"
                        placeholder="Additional operator instructions..."
                    />
                </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-zinc-100 pt-4">
                <Button
                    onClick={() => handleCreateOrder('draft')}
                    disabled={submitting}
                    className="bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-xs px-4 py-2 border border-zinc-300"
                >
                    <FileCheck size={14} className="mr-1 inline" /> Save as Draft
                </Button>
                <Button
                    onClick={() => handleCreateOrder('confirmed')}
                    disabled={submitting}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-5 py-2 font-medium"
                >
                    {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1 inline" />} Dispatch Order (Confirmed)
                </Button>
            </div>
        </Card>
    );
};